/**
 * RÓTULOS DE SITUAÇÃO EM PORTUGUÊS — tradução única para a tela.
 *
 * O sistema é operado em português, mas dezenas de telas imprimiam o CÓDIGO
 * do domínio direto: `ACTIVE`, `IN_PROGRESS`, `OUT_OF_SERVICE`,
 * `REPORT_ISSUED`. Quem usa o sistema lia inglês técnico no meio de uma
 * tabela em português, e `OUT_OF_SERVICE` não diz a um inspetor o que
 * "Fora de operação" diz.
 *
 * ── O CÓDIGO NÃO MUDA ─────────────────────────────────────────────────────
 *
 * Só a APRESENTAÇÃO é traduzida. Os valores persistidos, comparados e
 * gravados continuam em inglês: traduzir o dado quebraria todo `===`,
 * todo `CHECK` de migration e todo registro já gravado. Rótulo é da tela;
 * código é do domínio.
 *
 * ── NÃO É UMA SEGUNDA FONTE ───────────────────────────────────────────────
 *
 * Onde já existe mapa oficial — `ENGINEERING_WORKFLOW_STATUS_LABELS`,
 * `PLH_APPROVAL_STATUS_LABELS`, `TECHNICAL_CORRECTION_STATUS_LABELS`,
 * `AVR_GENERAL_STATUS_LABELS` — ele continua mandando e **não** é
 * duplicado aqui. Este catálogo cobre as situações que não tinham rótulo
 * nenhum e por isso vazavam cruas.
 *
 * ── DESCONHECIDO APARECE, NÃO SOME ────────────────────────────────────────
 *
 * Um código sem tradução é devolvido como está, legível. Devolver "—" ou
 * vazio esconderia um estado real do documento e faria parecer que o campo
 * não foi preenchido.
 */

/** Situações genéricas de cadastro (Cliente, Empresa, Área, Profissional, OS…). */
export const RECORD_STATUS_LABELS: Readonly<Record<string, string>> = {
  ACTIVE: "Ativo",
  INACTIVE: "Inativo",
  /**
   * Entra também no dicionário genérico, e não só no de Equipamento: a
   * função `translateStatus` cai aqui quando quem chama não indicou o
   * dicionário específico, e sem esta linha um código CONHECIDO do sistema
   * voltava cru para a tela — o mesmo defeito que este módulo corrige.
   */
  OUT_OF_SERVICE: "Fora de operação",
  SUSPENDED: "Suspenso",
  BLOCKED: "Bloqueado",
  PENDING: "Pendente",
  OPEN: "Aberta",
  CLOSED: "Encerrada",
  CANCELLED: "Cancelada",
  CANCELED: "Cancelada",
  DELETED: "Excluído",
  ARCHIVED: "Arquivado"
};

/** Situação do EQUIPAMENTO — `EquipmentStatus`. */
export const EQUIPMENT_STATUS_LABELS: Readonly<Record<string, string>> = {
  ACTIVE: "Ativo",
  INACTIVE: "Inativo",
  // "Fora de operação" é o termo da operação; "OUT_OF_SERVICE" não é.
  OUT_OF_SERVICE: "Fora de operação"
};

/** Situação da INSPEÇÃO — `InspectionStatus` e os estados finais equivalentes. */
export const INSPECTION_STATUS_LABELS: Readonly<Record<string, string>> = {
  DRAFT: "Rascunho",
  SCHEDULED: "Programada",
  IN_PROGRESS: "Em andamento",
  COMPLETED: "Concluída",
  FINISHED: "Concluída",
  CLOSED: "Encerrada",
  CANCELLED: "Cancelada",
  REPORT_ISSUED: "Relatório emitido"
};

/** Situação de DOCUMENTO/relatório genérico, quando não há mapa próprio. */
export const DOCUMENT_STATUS_LABELS: Readonly<Record<string, string>> = {
  DRAFT: "Rascunho",
  IN_PROGRESS: "Em elaboração",
  IN_ELABORATION: "Em elaboração",
  READY_FOR_REVIEW: "Pronto para revisão",
  WAITING_PLH: "Aguardando PLH",
  AWAITING_PLH_REVIEW: "Aguardando análise do PLH",
  PLH_REVIEWING: "Em análise do PLH",
  CORRECTION_REQUESTED: "Correção solicitada",
  RETURNED: "Devolvido para correção",
  WAITING_SIGNATURE: "Aguardando assinatura",
  AWAITING_SIGNATURE: "Aguardando assinatura",
  SUBMITTED_TO_ENGINEERING: "Enviado à Engenharia",
  PLH_APPROVED: "Aprovado pelo PLH",
  APPROVED: "Aprovado",
  SIGNED: "Assinado",
  ISSUED: "Emitido",
  /** Também aqui, e não só no dicionário de Inspeção, pelo mesmo motivo do
   *  `OUT_OF_SERVICE`: é a cadeia de fallback que a tela usa por padrão. */
  REPORT_ISSUED: "Relatório emitido",
  FINALIZED: "Finalizado",
  COMPLETED: "Concluído",
  CANCELLED: "Cancelado",
  REJECTED: "Reprovado",
  ON_HOLD: "Em HOLD",
  EM_HOLD: "Em HOLD"
};

/**
 * Situação do RELATÓRIO DE INTEGRIDADE (RI), que tem ciclo próprio.
 *
 * Estava como mapa local dentro da tela de Engenharia, com `|| r.status`
 * atrás — isto é, uma segunda fonte de rótulos que imprimia o código cru
 * quando não tinha a entrada. Trazido para cá para haver um lugar só.
 */
export const INTEGRITY_REPORT_STATUS_LABELS: Readonly<Record<string, string>> = {
  DRAFT: "Rascunho",
  IN_ELABORATION: "Em elaboração",
  FINALIZED_BY_INSPECTOR: "Finalizado pelo Inspetor",
  SUBMITTED_TO_ENGINEERING: "Aguardando análise do PLH",
  UNDER_ENGINEERING_REVIEW: "Em análise do PLH",
  RETURNED: "Correção solicitada",
  IN_CORRECTION: "Correção em andamento",
  APPROVED: "Aprovado pelo PLH",
  AWAITING_SIGNATURE: "Aguardando assinatura",
  SIGNED: "Assinado",
  COMPLETED: "Finalizado",
  ISSUED: "Finalizado"
};

/** Situação do apontamento de RDO — `RdoStatus`. */
export const RDO_STATUS_LABELS: Readonly<Record<string, string>> = {
  DRAFT: "Rascunho",
  SUBMITTED: "Enviado",
  APPROVED: "Aprovado",
  ISSUED: "Emitido",
  CANCELLED: "Cancelado"
};

const text = (value: unknown): string => (value === null || value === undefined ? "" : String(value));

/**
 * Traduz um código de situação usando o dicionário indicado, caindo para os
 * genéricos. Código sem tradução volta **como está** — visível e legível —
 * em vez de virar traço ou vazio.
 */
export function translateStatus(
  value: unknown,
  dictionary: Readonly<Record<string, string>> = DOCUMENT_STATUS_LABELS
): string {
  const code = text(value).trim();
  if (!code) return "";
  const key = code.toUpperCase().replace(/[\s-]+/g, "_");
  return dictionary[key] ?? DOCUMENT_STATUS_LABELS[key] ?? RECORD_STATUS_LABELS[key] ?? code;
}

export const equipmentStatusLabel = (value: unknown): string => translateStatus(value, EQUIPMENT_STATUS_LABELS);
export const inspectionStatusLabel = (value: unknown): string => translateStatus(value, INSPECTION_STATUS_LABELS);
export const recordStatusLabel = (value: unknown): string => translateStatus(value, RECORD_STATUS_LABELS);
export const documentStatusLabel = (value: unknown): string => translateStatus(value, DOCUMENT_STATUS_LABELS);
export const rdoStatusLabel = (value: unknown): string => translateStatus(value, RDO_STATUS_LABELS);
